import { Button, colors, fonts, radii, spacing } from '@steelwake/ui';

import { AccessibilitySection } from './AccessibilitySection.js';
import { ControlsSection } from './ControlsSection.js';
import { Row, Section } from './layout.js';

import {
  EFFECT_KEYS,
  matchesPreset,
  PRESET_NAMES,
  SHADOW_QUALITIES,
  type EffectKey,
  type FpsCap,
  type PresetName,
  type ShadowQuality,
} from './presets.js';
import { useSettingsStore } from './store.js';

const EFFECT_LABELS: Record<EffectKey, string> = {
  taa: 'Temporal anti-aliasing + upscale (TAAU)',
  ao: 'Ambient occlusion (GTAO)',
  ssgi: 'Screen-space global illumination',
  bloom: 'Bloom',
  dof: 'Depth of field (hangar)',
  motionBlur: 'Motion blur',
  lut: 'Colour grading',
  vignette: 'Vignette',
  chromaticAberration: 'Chromatic aberration',
  grain: 'Film grain',
};

const AUDIO_BUSES = ['master', 'sfx', 'music', 'ui'] as const;
const AUDIO_LABELS: Record<(typeof AUDIO_BUSES)[number], string> = {
  master: 'Master volume',
  sfx: 'Effects',
  music: 'Music',
  ui: 'Interface',
};

const PRESET_LABELS: Record<PresetName, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  ultra: 'Ultra',
};

export interface SettingsMenuProps {
  backend: string | null;
  onResume: () => void;
}

/** The in-arena settings menu (Esc): presets, render scale, dynamic resolution, effects. */
export function SettingsMenu({ backend, onResume }: SettingsMenuProps) {
  const settings = useSettingsStore((s) => s.settings);
  const setPreset = useSettingsStore((s) => s.setPreset);
  const update = useSettingsStore((s) => s.update);
  const setEffect = useSettingsStore((s) => s.setEffect);
  const active = matchesPreset(settings);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      data-testid="settings-menu"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(11, 13, 16, 0.55)',
        fontFamily: fonts.ui,
      }}
    >
      <div
        style={{
          width: 'min(600px, 94vw)',
          maxHeight: '92vh',
          overflowY: 'auto',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          padding: spacing.lg,
          color: colors.text,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 id="settings-title" style={{ margin: 0, fontSize: 20, letterSpacing: '0.12em' }}>
            Settings
          </h1>
          <span style={{ color: colors.textMuted, fontSize: 13 }} data-testid="settings-backend">
            {backend === 'webgpu' ? 'WebGPU' : backend === 'webgl2' ? 'WebGL 2' : '…'}
          </span>
        </div>

        <Section title="Quality preset">
          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            {PRESET_NAMES.map((name) => (
              <Button
                key={name}
                variant={active === name ? 'primary' : 'ghost'}
                aria-pressed={active === name}
                data-testid={`preset-${name}`}
                onClick={() => setPreset(name)}
              >
                {PRESET_LABELS[name]}
              </Button>
            ))}
            {active === 'custom' ? (
              <span style={{ alignSelf: 'center', color: colors.textMuted, fontSize: 13 }}>
                custom (based on {PRESET_LABELS[settings.preset]})
              </span>
            ) : null}
          </div>
        </Section>

        <Section title="Resolution and frame rate">
          <Row
            label={
              settings.dynamicResolution
                ? `Maximum render scale · ${settings.renderScale.toFixed(2)}`
                : `Render scale · ${settings.renderScale.toFixed(2)}`
            }
          >
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.01}
              value={settings.renderScale}
              data-testid="render-scale"
              onChange={(e) => update({ renderScale: Number(e.target.value) })}
            />
          </Row>
          <Row label="Dynamic resolution scaling (holds the frame budget)">
            <input
              type="checkbox"
              checked={settings.dynamicResolution}
              data-testid="drs"
              onChange={(e) => update({ dynamicResolution: e.target.checked })}
            />
          </Row>
          <Row label="Frame rate cap">
            <select
              value={settings.fpsCap}
              data-testid="fps-cap"
              onChange={(e) => update({ fpsCap: Number(e.target.value) as FpsCap })}
            >
              <option value={60}>60 fps</option>
              <option value={120}>120 fps</option>
              <option value={0}>Uncapped</option>
            </select>
          </Row>
          <Row label="Shadows">
            <select
              value={settings.shadows}
              data-testid="shadows"
              onChange={(e) => update({ shadows: e.target.value as ShadowQuality })}
            >
              {SHADOW_QUALITIES.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </Row>
          <Row label={`Field of view · ${String(settings.fov)}°`}>
            <input
              type="range"
              min={60}
              max={110}
              step={1}
              value={settings.fov}
              data-testid="fov"
              onChange={(e) => update({ fov: Number(e.target.value) })}
            />
          </Row>
        </Section>

        <Section title="Effects">
          {EFFECT_KEYS.map((key) => (
            <Row key={key} label={EFFECT_LABELS[key]}>
              <input
                type="checkbox"
                checked={settings.effects[key]}
                data-testid={`effect-${key}`}
                onChange={(e) => setEffect(key, e.target.checked)}
              />
            </Row>
          ))}
        </Section>

        <Section title="Audio">
          {AUDIO_BUSES.map((bus) => (
            <Row
              key={bus}
              label={`${AUDIO_LABELS[bus]} · ${String(Math.round(settings.audio[bus] * 100))}%`}
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={settings.audio[bus]}
                data-testid={`audio-${bus}`}
                onChange={(e) =>
                  update({ audio: { ...settings.audio, [bus]: Number(e.target.value) } })
                }
              />
            </Row>
          ))}
        </Section>

        <Section title="Controls">
          <Row label={`Mouse sensitivity · ${settings.mouseSensitivity.toFixed(2)}`}>
            <input
              type="range"
              min={0.2}
              max={3}
              step={0.05}
              value={settings.mouseSensitivity}
              data-testid="mouse-sensitivity"
              onChange={(e) => update({ mouseSensitivity: Number(e.target.value) })}
            />
          </Row>
          <Row label="Damage numbers">
            <input
              type="checkbox"
              checked={settings.damageNumbers}
              data-testid="damage-numbers"
              onChange={(e) => update({ damageNumbers: e.target.checked })}
            />
          </Row>
        </Section>

        <ControlsSection />
        <AccessibilitySection />

        <Section title="Renderer and overlay">
          <Row label="Force WebGL 2 (applies after reload)">
            <input
              type="checkbox"
              checked={settings.forceWebGL}
              data-testid="force-webgl"
              onChange={(e) => update({ forceWebGL: e.target.checked })}
            />
            <Button variant="ghost" data-testid="reload" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </Row>
          <Row label="Show frame-time overlay (F3)">
            <input
              type="checkbox"
              checked={settings.showStats}
              data-testid="show-stats"
              onChange={(e) => update({ showStats: e.target.checked })}
            />
          </Row>
          <Row label="Show connection readout online (ping, lead, corrections)">
            <input
              type="checkbox"
              checked={settings.showNetStats}
              data-testid="show-net-stats"
              onChange={(e) => update({ showNetStats: e.target.checked })}
            />
          </Row>
        </Section>

        <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.lg }}>
          <Button data-testid="resume" onClick={onResume}>
            Resume
          </Button>
          <Button
            variant="ghost"
            data-testid="reset-preset"
            onClick={() => setPreset(settings.preset)}
          >
            Reset to {PRESET_LABELS[settings.preset]}
          </Button>
        </div>
      </div>
    </div>
  );
}
